import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { FaEdit, FaTrashAlt, FaUserShield } from 'react-icons/fa'
import { useSelector } from 'react-redux'
import { useAsyncDebounce } from 'react-table'
import 'react-toastify/dist/ReactToastify.css'

import { useGetCompanyGroupsQuery } from '../../../api/services/groupsApi'
import { useGetCompanyUsersPaginatedQuery } from '../../../api/services/usersApi'
import useDarkMode from '../../../hooks/useDarkMode'
import AssignPolicyModal from '../../modals/companies/policies/AssignPolicyModal'
import { CreateUserInCompanyModal } from '../../modals/modals'
import { EditCompanyModal } from '../../modals/modals'
import DeleteUserModal from '../../modals/users/DeleteUserModal'
import EditUserModal from '../../modals/users/EditUserModal'
import { Button } from '../../root/Button'
import ColumnFilterDatePagination from '../components/ColumnFilterDatePagination'
import NewTable from '../NewTable'

const TableCompanyUsers = () => {
  const { t } = useTranslation()

  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(25)
  const [filterInputs, setFilterInputs] = useState({})

  const handleFilterChange = useAsyncDebounce((value, columnId) => {
    setPage(1)
    setFilterInputs((prev) => {
      const newValue = value.trim() || ' '
      return { ...prev, [columnId]: newValue }
    })
  }, 50)

  const columns = useMemo(
    () => [
      {
        Header: t('first_name'),
        accessor: 'firstName',
      },
      {
        Header: t('last_name'),
        accessor: 'lastName',
      },
      {
        Header: 'Email',
        accessor: 'email',
      },
      {
        Header: t('username'),
        accessor: 'username',
      },
      {
        Header: t('group'),
        accessor: 'name',
        Cell: ({ row }) => {
          return (
            <div className="flex w-full justify-center">
              <p className="mb-0">{row.original.name || t('no_group')}</p>
            </div>
          )
        },
      },
      {
        Header: t('category_access_policy'),
        accessor: 'policyName',
        Cell: ({ row }) => row.original.policyName || t('no_policy_assigned'),
      },
      {
        Header: t('arch_access'),
        accessor: 'archive',
      },
      {
        Header: t('created2'),
        accessor: 'createdAt',
        Cell: ({ row }) => {
          const createdAt = row.original.createdAt
          if (!createdAt) return ''
          return createdAt.substr(0, 10)
        },
        Filter: ({ column }) => {
          return <ColumnFilterDatePagination column={column} handleFilterChange={handleFilterChange} />
        },
      },
    ],
    [t, handleFilterChange],
  )

  useDarkMode()

  const [openDelete, setOpenDelete] = useState(false)
  const [openEdit, setOpenEdit] = useState(false)
  const [openEditCopany, setOpenEditCopany] = useState(false)
  const [openAssignPolicy, setOpenAssignPolicy] = useState(false)
  const [user, setUser] = useState({})
  const [newUser, setNewUser] = useState({})
  const [newGroup, setGroup] = useState()
  const [recipient, setRecipient] = useState()
  const [warning, setWarning] = useState()
  const [emailNotification, setEmailNotification] = useState()
  const [archive, setArchive] = useState()
  const [permissions, setPermissions] = useState([])

  const [openRegisterUser, setOpenRegisterUser] = useState(false)

  const [newCompnay, setNewCompany] = useState({})
  const [tax, setTax] = useState()
  const [companyState, setCompanyState] = useState({})

  const company = useSelector((state) => state.company.company)

  const query = useMemo(() => {
    let queryString = ''
    for (const [key, value] of Object.entries(filterInputs)) {
      if (key === 'createdAt') {
        if (value.trim() !== '') {
          queryString += `createdAt=${encodeURIComponent(value.trim())}&`
        }
      } else if (value.trim() !== '') {
        queryString += `${key}=${encodeURIComponent(value)}&`
      }
    }

    return queryString.replace(/&$/, '')
  }, [filterInputs])

  const { data: paginatedUsers, isFetching, isLoading } = useGetCompanyUsersPaginatedQuery(
    {
      companyId: company?.id,
      page,
      pageSize,
      query: query || '',
    },
    { skip: !company?.id },
  )

  const { data: companyGroups } = useGetCompanyGroupsQuery(company?.id, {
    skip: !company?.id,
  })

  const handleClose = () => {
    setGroup([])
    setUser({})
    setRecipient()
    setWarning()
    setEmailNotification()
  }

  const permissionOptions = companyGroups
    ? companyGroups.map((companyGroup) => ({
        value: companyGroup?.id,
        label: companyGroup?.name,
      }))
    : []

  const data = useMemo(
    () => (Array.isArray(paginatedUsers?.users) ? paginatedUsers.users : []),
    [paginatedUsers],
  )

  const tableHooksArray = [
    {
      label: t('edit'),
      Icon: FaEdit,
      onClick: (row) => {
        setOpenEdit(true)
        setUser(row.original)
        setRecipient({ label: row.original.recipient === 1 ? 'True' : 'False', value: row.original.recipient })
        setWarning({ label: row.original.warning === 1 ? 'True' : 'False', value: row.original.warning })
        setEmailNotification({
          label: row.original.emailNotification === 1 ? 'True' : 'False',
          value: row.original.emailNotification,
        })
        setGroup({ label: row.original.name, value: row.original.groupId })
      },
      disabled: false,
    },
    {
      label: t('delete'),
      Icon: FaTrashAlt,
      onClick: (row) => {
        setOpenDelete(true)
        setUser(row.original)
      },
      disabled: false,
    },
    {
      label: t('assign_policy'),
      Icon: FaUserShield,
      onClick: (row) => {
        setUser(row.original)
        setOpenAssignPolicy(true)
      },
      disabled: false,
    },
  ]

  return (
    <>
      <NewTable
        data={data}
        columns={columns}
        actions
        filtering
        hooksArray={tableHooksArray}
        setPage={setPage}
        pageManual={page}
        pageSize={pageSize}
        setPageAmount={setPageSize}
        controlledPageCount={paginatedUsers?.totalPages || 0}
        filterInputs={filterInputs}
        setFilterInputs={setFilterInputs}
        count={paginatedUsers?.totalCount}
        isFetching={isFetching}
        isLoading={isLoading}
      />
      <div className="w-full flex">
        <div className="w-full">
          <div className="flex gap-4 py-4">
            <Button
              onClick={() => {
                setOpenRegisterUser(!openRegisterUser)
              }}
            >
              {t('reg_user')}
            </Button>
            <Button
              onClick={() => {
                setOpenEditCopany(true)
                setTax(company.tax)
                setCompanyState(company)
              }}
            >
              {t('edit_company')}
            </Button>
          </div>
        </div>
      </div>

      <CreateUserInCompanyModal
        setPermissions={setPermissions}
        companyGroups={permissionOptions}
        onClose={() => {
          setRecipient()
          setWarning()
          setEmailNotification()
          setOpenRegisterUser(false)
        }}
        setRecipient={setRecipient}
        permissions={permissions}
        setArchive={setArchive}
        recipient={recipient}
        company={company}
        setUser={setUser}
        archive={archive}
        open={openRegisterUser}
        user={user}
        warning={warning}
        setWarning={setWarning}
        setEmailNotification={setEmailNotification}
        emailNotification={emailNotification}
      />
      <DeleteUserModal user={user} open={openDelete} companyId={company?.id} onClose={() => setOpenDelete(false)} />
      <EditUserModal
        onClose={() => {
          setOpenEdit(false)
          handleClose()
        }}
        setRecipient={setRecipient}
        companyId={company?.id}
        setArchive={setArchive}
        setNewUser={setNewUser}
        permissionOptions={permissionOptions}
        handleClose={handleClose}
        isPermissions
        recipient={recipient}
        stateUser={newUser}
        newGroup={newGroup}
        setGroup={setGroup}
        archive={archive}
        companyUsers={true}
        setUser={setUser}
        open={openEdit}
        user={user}
        warning={warning}
        setWarning={setWarning}
        isEditInCompany
        setEmailNotification={setEmailNotification}
        emailNotification={emailNotification}
      />
      <EditCompanyModal
        onClose={() => setOpenEditCopany(false)}
        setNewCompany={setNewCompany}
        newCompany={newCompnay}
        company={companyState}
        open={openEditCopany}
        setTax={setTax}
        tax={tax}
      />
      <AssignPolicyModal open={openAssignPolicy} onClose={() => setOpenAssignPolicy(false)} user={user} />
    </>
  )
}
export default TableCompanyUsers
